import React from "react";
import { fmtKwp } from "../../lib/format";
import { CLASSIFICATIONS, COMBINED_META, CLASS_KEYS, resolveClass } from "../../lib/classification";

/**
 * BusinessClassToggle — Retro / Development / Customer / Combined switcher.
 *
 * Every project carries a `business_classification` string. This toggle
 * drives the Lifecycle Board's class-scope filter and shows live kWp +
 * project counts for each classification so admins can flip between the
 * class books at a glance. Class list + tones now come from
 * `lib/classification` so adding a new class is a one-file edit.
 */
export default function BusinessClassToggle({ data, classFilter, setClassFilter }) {
  const totals = React.useMemo(() => {
    const t = { combined: { count: 0, kwp: 0 } };
    for (const key of CLASS_KEYS) t[key] = { count: 0, kwp: 0 };
    // Ops wants completed sites counted at the portfolio level, so no
    // terminal-stage exclusion happens here — every project on the
    // Kanban contributes to its class total.
    for (const [, projs] of Object.entries(data.projects_by_status || {})) {
      for (const p of projs) {
        const cls = resolveClass(p.business_classification);
        const kwp = Number(p.system_size_kwp) || 0;
        if (t[cls]) { t[cls].count++; t[cls].kwp += kwp; }
        t.combined.count++; t.combined.kwp += kwp;
      }
    }
    return t;
  }, [data]);

  const opts = [
    { v: "combined", l: COMBINED_META.label, sub: COMBINED_META.sub, tone: COMBINED_META.tone },
    ...CLASSIFICATIONS.map(c => ({ v: c.key, l: c.label, sub: c.sub, tone: c.tone })),
  ];
  return (
    <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="pm-class-toggle">
      {opts.map(opt => {
        const active = classFilter === opt.v;
        const t = totals[opt.v];
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => setClassFilter(opt.v)}
            className={`p-3 rounded-lg border-2 text-left transition-all ${
              active ? "shadow-sm" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
            }`}
            style={active ? { borderColor: opt.tone, background: `${opt.tone}15` } : undefined}
            data-testid={`pm-class-${opt.v}`}
            aria-pressed={active}
          >
            <div className="flex items-baseline justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: opt.tone }}>
                  {opt.l}
                </div>
                <div className="text-[10px] muted">{opt.sub}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-bold leading-tight" style={{ color: opt.tone }}>
                  {fmtKwp(t.kwp)}
                </div>
                <div className="text-[10px] muted mt-0.5">{t.count} project{t.count === 1 ? "" : "s"}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
